import type { Asset, DependencyEdge, ScenarioOutput } from '@/types';
import { runCascade } from './cascade';

export interface ContingencyResult {
  failed_assets: string[];
  total_assets_impacted: number;
  economic_impact_eur_m: number;
  population_impacted: number;
  loss_p50: number;
}

/**
 * N-1 contingency: enumerate every single-asset failure and return ranked impact.
 * Ranks by total impacted asset count × economic impact.
 */
export function nMinus1(
  assets: Asset[],
  edges: DependencyEdge[],
  candidate_filter?: (a: Asset) => boolean,
): ContingencyResult[] {
  const candidates = candidate_filter ? assets.filter(candidate_filter) : assets;
  const results: ContingencyResult[] = [];
  for (const a of candidates) {
    const out = runCascade(
      {
        scenario_id: `nm1-${a.asset_id}`,
        name: `N-1: ${a.name}`,
        mode: 'failure',
        initial_failed_assets: [a.asset_id],
        created_at: new Date().toISOString(),
      },
      assets,
      edges,
    );
    results.push(toResult([a.asset_id], out));
  }
  return results.sort((x, y) => severityIndex(y) - severityIndex(x));
}

/**
 * N-2 contingency: enumerate all pairs (or pre-filtered subset of pairs).
 * For practicality we restrict to top-K critical assets.
 */
export function nMinus2(
  assets: Asset[],
  edges: DependencyEdge[],
  top_k = 12,
): ContingencyResult[] {
  const top = [...assets]
    .filter((a) => a.criticality_rating >= 4)
    .sort((a, b) => b.criticality_rating - a.criticality_rating)
    .slice(0, top_k);
  const results: ContingencyResult[] = [];
  for (let i = 0; i < top.length; i++) {
    for (let j = i + 1; j < top.length; j++) {
      const a = top[i]!;
      const b = top[j]!;
      const out = runCascade(
        {
          scenario_id: `nm2-${a.asset_id}-${b.asset_id}`,
          name: `N-2: ${a.name} + ${b.name}`,
          mode: 'failure',
          initial_failed_assets: [a.asset_id, b.asset_id],
          created_at: new Date().toISOString(),
        },
        assets,
        edges,
      );
      results.push(toResult([a.asset_id, b.asset_id], out));
    }
  }
  return results.sort((x, y) => severityIndex(y) - severityIndex(x));
}

/**
 * Adversarial minimum-cut analysis: greedy heuristic to find the smallest set
 * of assets whose simultaneous failure achieves a target impact level
 * (measured by impacted-asset count). At each step, choose the asset whose
 * failure adds the most marginal impact.
 */
export function minimumCut(
  assets: Asset[],
  edges: DependencyEdge[],
  target_impacted_count: number,
  max_size = 6,
  candidate_filter?: (a: Asset) => boolean,
): ContingencyResult {
  const candidates = (candidate_filter ? assets.filter(candidate_filter) : assets).map(
    (a) => a.asset_id,
  );
  const chosen: string[] = [];
  let lastResult: ScenarioOutput | null = null;

  for (let step = 0; step < max_size; step++) {
    let bestId: string | null = null;
    let bestImpact = -1;
    let bestResult: ScenarioOutput | null = null;
    for (const id of candidates) {
      if (chosen.includes(id)) continue;
      const trial = [...chosen, id];
      const out = runCascade(
        {
          scenario_id: `mincut-${trial.join('-')}`,
          name: `MinCut ${trial.length}`,
          mode: 'attack_physical',
          initial_failed_assets: trial,
          created_at: new Date().toISOString(),
        },
        assets,
        edges,
      );
      if (out.total_assets_impacted > bestImpact) {
        bestImpact = out.total_assets_impacted;
        bestId = id;
        bestResult = out;
      }
    }
    if (!bestId || !bestResult) break;
    chosen.push(bestId);
    lastResult = bestResult;
    if (bestResult.total_assets_impacted >= target_impacted_count) break;
  }

  if (!lastResult) {
    return {
      failed_assets: [],
      total_assets_impacted: 0,
      economic_impact_eur_m: 0,
      population_impacted: 0,
      loss_p50: 0,
    };
  }
  return toResult(chosen, lastResult);
}

function toResult(failed: string[], out: ScenarioOutput): ContingencyResult {
  return {
    failed_assets: failed,
    total_assets_impacted: out.total_assets_impacted,
    economic_impact_eur_m: out.economic_impact_eur_m ?? 0,
    population_impacted: out.population_impacted_estimate ?? 0,
    loss_p50: out.confidence_intervals?.p50 ?? 0,
  };
}

function severityIndex(r: ContingencyResult): number {
  return r.total_assets_impacted * 100 + r.economic_impact_eur_m + r.loss_p50;
}
